import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CONSENT_KEY, CONSENT_EVENT, getConsent, setConsent, disablePostHog, enablePostHog } from "../lib/consent";

/**
 * ConsentBanner — appears once, at page bottom, until visitor picks.
 *
 * Rules from Batch 2 §1:
 *   - No pre-ticked boxes.
 *   - Accept and Reject are visually identical (same size, weight, prominence).
 *   - Nothing loads from ap.emergent.sh (PostHog) until Accept.
 *   - Session recording stays off in either case (separate opt-in).
 *   - Choice persists (localStorage). Preferences link in footer lets people change it.
 */
export default function ConsentBanner() {
  const [choice, setChoice] = useState(() => getConsent());
  const [ready, setReady] = useState(false);

  // Poll for PostHog init & apply consent immediately.
  useEffect(() => {
    // Give the framework a tick to breathe before we start disabling globals.
    const t = setTimeout(() => setReady(true), 250);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (choice === "accepted") enablePostHog();
    else disablePostHog(); // covers null (undecided) + "rejected"
  }, [choice, ready]);

  // Listen for programmatic changes (from Preferences link in footer)
  useEffect(() => {
    const onChange = (e) => setChoice(e.detail);
    window.addEventListener(CONSENT_EVENT, onChange);
    // Also sync across tabs
    const onStorage = (e) => { if (e.key === CONSENT_KEY) setChoice(e.newValue); };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const accept = () => { setConsent("accepted"); setChoice("accepted"); };
  const reject = () => { setConsent("rejected"); setChoice("rejected"); };

  if (choice === "accepted" || choice === "rejected") return null;

  return (
    <div
      role="dialog"
      aria-label="Cookies and analytics"
      aria-modal="false"
      data-testid="consent-banner"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-[#343437] bg-[#0B0B0C]/97 backdrop-blur-sm"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
        <div className="flex-1">
          <p className="font-display text-[13px] tracking-[0.14em] uppercase text-[#D8A936]">
            Cookies & analytics
          </p>
          <p className="mt-2 text-sm text-[#C7C4BC] leading-relaxed max-w-2xl">
            This site uses privacy-conscious analytics (PostHog) to understand which pages help visitors most.
            No third-party advertising, no session recording, no data sold on.
            {" "}
            <Link to="/privacy" className="underline text-[#D8A936] hover:text-[#FAF9F6]" data-testid="consent-privacy-link">
              Read the privacy policy
            </Link>.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={reject}
            data-testid="consent-reject"
            className="px-5 py-2.5 border border-[#343437] text-[11px] tracking-[0.18em] uppercase font-display text-[#FAF9F6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={accept}
            data-testid="consent-accept"
            className="px-5 py-2.5 border border-[#D8A936] bg-[#D8A936] text-[11px] tracking-[0.18em] uppercase font-display text-[#0B0B0C] hover:bg-[#B98B24] hover:border-[#B98B24] transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
